import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { QueryItemRecoveryAggregate } from '../../api/schema';
import { DatePicker } from '../../component/datePicker/DatePicker';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import Table from '../../component/table/Table';
import { tagsDate } from '../../helpers/dataHelper';
import useSearchParams from '../../hooks/useSearchParams';
import useTablePagination from '../../hooks/useTablePagination';
import { getItemsRecoveryProgressById, getItemsRecoveryProgressFetching, listItemsRecoveryProgress } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';

type ToolsRecoveryProgressAggregateProps = {
  dense?: boolean;
  title?: string;
  headerAddon?: ReactElement;
  withExport?: boolean;
  defaultPageSize?: number;
};

export const ToolsRecoveryProgressAggregate: React.FC<ToolsRecoveryProgressAggregateProps> = ({
  dense,
  title,
  headerAddon,
  withExport,
  defaultPageSize,
}: ToolsRecoveryProgressAggregateProps) => {
  const dispatch = useDispatch();
  const [params, setURLParams] = useSearchParams();
  const selectedDate = useMemo(() => {
    if (params.date) {
      const dt = DateTime.fromISO(params.date);
      if (dt.isValid) return dt.toJSDate();
    }
    return new Date();
  }, [params.date]);
  const selectedEndDate = useMemo(() => {
    if (params.dateEnd) {
      const dt = DateTime.fromISO(params.dateEnd);
      if (dt.isValid) return dt.toJSDate();
    }
    return null;
  }, [params.dateEnd]);
  const todayStart = useMemo(() => DateTime.fromJSDate(selectedDate).startOf('day').toUTC().toISO() ?? '', [selectedDate]);
  const todayEnd = useMemo(
    () => (selectedEndDate ? DateTime.fromJSDate(selectedEndDate).endOf('day').toUTC().toISO() ?? undefined : undefined),
    [selectedEndDate],
  );
  const sortBy = params.sortBy || '-last_scan_at';
  const fetching = useSelector(getItemsRecoveryProgressFetching);
  const items = useSelector(getItemsRecoveryProgressById({ todayStart, todayEnd, ...params, sortBy }));
  const [pagination, paginationToGet, paginationControl, paginationToGetAll] = useTablePagination({
    paginationDirty: items.pagination,
    defaultLimit: defaultPageSize,
  });
  const itemsExport = useSelector(getItemsRecoveryProgressById({ todayStart, todayEnd, ...params, sortBy, ...paginationToGetAll }));
  const totals = useMemo(
    () => ({
      original_items_out: items.totals?.original_items_out ?? 0,
      recovered_today: items.totals?.recovered_today ?? 0,
      outstanding_now: items.totals?.outstanding_now ?? 0,
    }),
    [items.totals],
  );

  useEffect(() => {
    dispatch(listItemsRecoveryProgress({ todayStart, todayEnd, ...params, sortBy, ...paginationToGet }));
  }, [dispatch, params, paginationToGet, sortBy, todayStart, todayEnd]);

  const getExportData = useCallback(async () => {
    await dispatch(
      listItemsRecoveryProgress({
        todayStart,
        todayEnd,
        ...params,
        sortBy,
        ...paginationToGetAll,
      }),
    );
  }, [dispatch, params, paginationToGetAll, sortBy, todayStart, todayEnd]);

  const columns = useMemo(
    () => [
      {
        Header: 'Job Site',
        accessor: 'site_name',
        ellipsis: true,
        minWidth: 140,
        Cell: ({ row }: { row: { original: QueryItemRecoveryAggregate } }) => (
          <Link to={`/customers/job-sites/${row.original.site_id}/details`}>{row.original.site_name}</Link>
        ),
      },
      {
        // The client's own reference for the job; optional, so most rows are blank.
        Header: 'Job #',
        accessor: 'job_number',
        width: 90,
        Cell: ({ row }: { row: { original: QueryItemRecoveryAggregate } }) => row.original.job_number || '',
      },
      {
        Header: 'Location',
        accessor: 'address',
        ellipsis: true,
        minWidth: 160,
        Cell: ({ row }: { row: { original: QueryItemRecoveryAggregate } }) =>
          [row.original.address, row.original.city, row.original.state].filter(Boolean).join(', '),
        getCellExportValue: (row: { original: QueryItemRecoveryAggregate }) =>
          [row.original.address, row.original.city, row.original.state].filter(Boolean).join(', '),
      },
      {
        Header: 'Delivered',
        accessor: 'delivered_at',
        width: '15%',
        minWidth: 92,
        ellipsis: true,
        Cell: ({ row }: { row: { original: QueryItemRecoveryAggregate } }) =>
          row.original.delivered_at ? tagsDate(row.original.delivered_at, 'TIME_SIMPLE', 'DATE_MED') : '',
        getCellExportValue: (row: { original: QueryItemRecoveryAggregate }) =>
          row.original.delivered_at ? tagsDate(row.original.delivered_at, 'DATE_MED', 'DATE_MED') : '',
      },
      {
        Header: 'Last Scan',
        accessor: 'last_scan_at',
        width: '15%',
        minWidth: 92,
        ellipsis: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }: { row: { original: QueryItemRecoveryAggregate } }) =>
          // eslint-disable-next-line react/prop-types
          row.original.last_scan_at ? tagsDate(row.original.last_scan_at, 'TIME_SIMPLE', 'DATETIME_MED') : '',
        getCellExportValue: (row: { original: QueryItemRecoveryAggregate }) =>
          row.original.last_scan_at ? tagsDate(row.original.last_scan_at, 'DATETIME_MED', 'DATETIME_MED') : '',
      },
      {
        Header: 'Original',
        accessor: 'original_items_out',
        width: '6%',
        minWidth: 36,
        style: { textAlign: 'center' },
      },
      {
        Header: 'Recovered',
        accessor: 'recovered_today',
        width: '9%',
        minWidth: 50,
        style: { textAlign: 'center' },
      },
      {
        Header: 'Outstanding',
        accessor: 'outstanding_now',
        width: '7.5%',
        minWidth: 44,
        style: { textAlign: 'center' },
      },
      {
        Header: 'Cleared',
        accessor: 'cleared',
        width: '10%',
        minWidth: 72,
        style: { textAlign: 'center' },
        Cell: ({ row }: { row: { original: QueryItemRecoveryAggregate } }) =>
          row.original.outstanding_now === 0 ? <CheckCircleIcon color="success" sx={{ fontSize: 19 }} /> : null,
        getCellExportValue: (row: { original: QueryItemRecoveryAggregate }) => (row.original.outstanding_now === 0 ? 'Yes' : ''),
      },
    ],
    [],
  );

  return (
    <>
      {fetching && <LinearProgress />}

      <Table
        title={title}
        columns={columns}
        data={items.result || []}
        loading={fetching}
        toolbarDense="medium"
        size={dense ? 'small' : 'medium'}
        noGlobalFilter
        paginationControl={paginationControl}
        pagination={pagination}
        totals={totals}
        headerAddon={headerAddon}
        filtersAddon={
          <Box sx={{ display: 'flex', gap: 4, mr: 12, pt: 3 }}>
            <Box sx={{ width: 160 }}>
              <DatePicker
                label="Begin Date"
                value={selectedDate}
                onChange={(v) => {
                  if (v instanceof Date && !isNaN(v.getTime())) {
                    const begin = DateTime.fromJSDate(v).startOf('day');
                    const newParams: { date: string; dateEnd?: string } = { date: begin.toFormat('yyyy-MM-dd') };
                    if (selectedEndDate && DateTime.fromJSDate(selectedEndDate).startOf('day') < begin) {
                      newParams.dateEnd = begin.toFormat('yyyy-MM-dd');
                    }
                    setURLParams({ newParams });
                  }
                }}
              />
            </Box>
            <Box sx={{ width: 160 }}>
              <DatePicker
                label="End Date"
                value={selectedEndDate}
                withClear
                onChange={(v) => {
                  if (v === null) {
                    setURLParams({ newParams: { dateEnd: undefined } });
                    return;
                  }
                  if (v instanceof Date && !isNaN(v.getTime())) {
                    let end = DateTime.fromJSDate(v).startOf('day');
                    const begin = DateTime.fromJSDate(selectedDate).startOf('day');
                    if (end < begin) {
                      end = begin;
                    }
                    setURLParams({ newParams: { dateEnd: end.toFormat('yyyy-MM-dd') } });
                  }
                }}
              />
            </Box>
          </Box>
        }
        {...(withExport && { dataExport: itemsExport.result, getExportData, exportFileName: 'RecoveryProgress' })}
      />
    </>
  );
};
